import React, { useState, useEffect } from "react"
import { useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { getLivisToken } from "../../helpers/storage"
import { Fab, Box, Zoom } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { LivisCopilotIcon } from "../Icons/LivisIcons"
import AIChatWindow, { ChatMode, Message } from "./chat"
import "./styles/ai-agent.scss"

const INITIAL_MESSAGES: Message[] = []

const AIAgentWidget: React.FC = () => {
  const { t } = useTranslation()
  // Persist open state in sessionStorage — chat stays open across page navigations until user closes it
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    return sessionStorage.getItem("livis_chat_open") === "true"
  })
  const [mode, setMode] = useState<ChatMode>(() => {
    const stored = sessionStorage.getItem(
      "livis_copilot_mode"
    ) as ChatMode | null
    if (stored === "fullscreen") return "fullscreen"
    return "floating"
  })

  useEffect(() => {
    if (sessionStorage.getItem("livis_copilot_mode") === "sidebar") {
      sessionStorage.setItem("livis_copilot_mode", "floating")
      setMode("floating")
    }
  }, [])

  useEffect(() => {
    sessionStorage.setItem("livis_copilot_mode", mode)
  }, [mode])

  // Lifted state — survives mode switches (floating ↔ fullscreen)
  const [messages, setMessages] = useState<Message[]>(INITIAL_MESSAGES)
  const [sessionId, setSessionId] = useState<string>(
    () => sessionStorage.getItem("livis_chat_session_id") || ""
  )
  const [isLoading, setIsLoading] = useState(false)
  const [currentStatus, setCurrentStatus] = useState("")
  const abortControllerRef = React.useRef<AbortController | null>(null)

  // Sync sessionId to sessionStorage so it survives page refresh
  React.useEffect(() => {
    if (sessionId) {
      sessionStorage.setItem("livis_chat_session_id", sessionId)
    } else {
      sessionStorage.removeItem("livis_chat_session_id")
    }
  }, [sessionId])

  const [showFab, setShowFab] = useState<boolean>(!isOpen)
  const [isFabHovered, setIsFabHovered] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setShowFab(false)
      document.body.classList.add("livis-copilot-open")
    } else {
      // Wait for Zoom close animation (300ms) before showing FAB and tour icon
      const timer = setTimeout(() => {
        setShowFab(true)
        document.body.classList.remove("livis-copilot-open")
      }, 320)
      return () => clearTimeout(timer)
    }
    return () => {
      document.body.classList.remove("livis-copilot-open")
    }
  }, [isOpen])

  const toggleOpen = () => {
    // When closing from fullscreen, reset to floating first so the wrapper's
    // `transform: none !important` (added by :has(.expanded) rule) is removed
    // before MUI Zoom's scale-out animation runs.
    if (isOpen && mode === "fullscreen") {
      setMode("floating")
    }
    setIsFabHovered(false)
    setIsOpen((prev) => {
      const next = !prev
      sessionStorage.setItem("livis_chat_open", String(next))
      return next
    })
  }
  // inputValue lives inside AIChatWindow now — keeping it here was causing
  // the entire tree (messages + markers) to re-render on every keystroke.
  const sharedProps = {
    messages,
    setMessages,
    sessionId,
    setSessionId,
    isLoading,
    setIsLoading,
    currentStatus,
    setCurrentStatus,
    abortControllerRef,
  }

  return (
    <Box className="livis-ai-assistant-container">
      {showFab && (
        <Fab
          className={`livis-ai-assistant-fab ${isOpen ? "active" : ""} ${
            isFabHovered && !isOpen ? "livis-ai-assistant-fab-hovered" : ""
          }`}
          onClick={toggleOpen}
          onMouseEnter={() => setIsFabHovered(true)}
          onMouseLeave={() => setIsFabHovered(false)}
          aria-label={
            isOpen
              ? (t("Close LIVIS Copilot", {
                  defaultValue: "Close LIVIS Copilot",
                }) as string)
              : (t("LIVIS Copilot", {
                  defaultValue: "LIVIS Copilot",
                }) as string)
          }
        >
          {isOpen ? (
            <Box className="livis-ai-fab-icon">
              <CloseIcon
                sx={{ color: "var(--livis-black-font-color) !important" }}
              />
            </Box>
          ) : (
            <>
              {isFabHovered && (
                <span className="floating-button-text livis-ai-fab-text">
                  {t("LIVIS Copilot", { defaultValue: "LIVIS Copilot" })}
                </span>
              )}
              <Box className="livis-ai-fab-icon">
                <LivisCopilotIcon colorType="white" />
              </Box>
            </>
          )}
        </Fab>
      )}

      <Zoom in={isOpen} unmountOnExit timeout={300}>
        {/* We wrap in a div because Zoom needs a ref-forwarding DOM element */}
        <div className="livis-ai-assistant-widget-wrapper">
          <AIChatWindow
            mode={mode}
            setMode={setMode}
            onClose={toggleOpen}
            {...sharedProps}
          />
        </div>
      </Zoom>
    </Box>
  )
}

const AIAgentWidgetWrapper: React.FC = () => {
  const location = useLocation()
  const [hasToken, setHasToken] = useState<boolean>(!!getLivisToken())

  useEffect(() => {
    setHasToken(!!getLivisToken())
  }, [location.pathname])

  if (!hasToken) {
    return null
  }

  return <AIAgentWidget />
}

export default AIAgentWidgetWrapper
